<template>
  <Dialog
    v-model="isOpen"
    v-bind="$attrs"
    max-width="40%"
    :persistent="dataLoading || saveInProgress"
    :content-class="'report-management-dialog'"
    :disabled="wrongDatatype"
    @save="save"
  >
    <template #content>
      <div
        v-if="dataLoading"
        class="text-center"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </div>
      <template v-else>
        <div v-if="orderStatusId === null">
          {{ t("Order not found") }}
        </div>
        <OrderStatus
          v-if="localOrder"
          :statuses="statuses"
          :order="localOrder"
          :save-in-progress="saveInProgress"
        />

        <FileInput
          v-model="files"
          :label="t('File input')"
          :multiple="true"
          :disabled="saveInProgress"
          @add-files="onFilesSelected"
          @invalid-files="onInvalidFiles"
        />

        <v-data-table
          :headers="headers"
          :items="combinedReports"
          :row-props="getRowProps"
          :no-data-text="t('No data available')"
          hide-default-footer
          density="compact"
          class="mt-4"
        >

          <template #item.fileName="{ item }">
            <FileTooltip
              v-if="item.fileName.length > 20"
              :file="item"
            >
              <template #content>
                <div class="file-name-ellipsis">
                  {{ item.fileName }}
                </div>
              </template>
            </FileTooltip>
            <div v-else>
              {{ item.fileName }}
            </div>
          </template>

          <template #item.comment="{ item }">
            <input
              v-model="item.comment"
              class="px-1"
              :readonly="!isCommentEditable"
              :placeholder="
                isCommentEditable
                  ? t('Add comment...')
                  : t('Read only')
              "
              :disabled="item.delete || saveInProgress"
            />
          </template>

          <template #item.updatedAt="{ item }" class="text-no-wrap">
            {{ normalizeDate(item.updatedAt, true) ?? '-' }}
          </template>

          <template #item.actions="{ index }">
            <Button
              icon-btn
              color="black"
              size="small"
              :disabled="saveInProgress"
              @click="toggleDeleteState(index)"
            >
              <v-icon >
                {{ combinedReports[index].delete ? 'mdi-undo' : 'mdi-delete-outline' }}
              </v-icon>
            </Button>
          </template>

          <template #no-data>
            <div class="text-center py-4">
              {{ t("No Reports Available") }}
            </div>
          </template>
        </v-data-table>

      </template>
    </template>

    <template
      v-if="dataLoading || orderStatusId === null"
      #actions
    >
      &nbsp;
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { watch, ref, computed } from "vue";
import Dialog from "@/components/dialogs/Dialog.vue";
import FileInput from "@/components/input-components/FileInput.vue";
import Button from "@/components/input-components/Button.vue";
import OrderStatus from "@/components/orders/OrderStatus.vue";
import FileTooltip from "@/components/common/FileTooltip.vue";
import {
  Report,
  ReportCreatePayload,
  ReportUpdatePayload,
  ReportDeletePayload,
  ReportManagementPayload,
  ReportUI
} from "@/entities/report";
import { Order } from "@/entities/order";
import { PERMISSION } from "@/utils/constants";
import { useI18n } from "vue-i18n"
import { usePermissionsService } from '@/composables/usePermissionsService';
import NotificationMessageService from "@/services/notificationmessage.service";
import {normalizeDate} from "@/composables/useDateHelper";

defineOptions({ name: "ReportManagementDialog" });

const props = defineProps({
  statuses: { type: Array, default: () => [] },
  order: { type: Object, default: null },
  dataLoading: { type: Boolean, required: true, default: false },
  modelValue: { type: Boolean, required: true },
  reports: { type: Array as () => any[], required: true, default: () => [] },
  saveInProgress: { type: Boolean, default: false },
});

const { t } = useI18n()
const permissionsService = usePermissionsService();
const isCommentEditable = computed(() => {
  return permissionsService.canEditReportComment;
});

const headers = computed(() => [
  { title: t("File name"), value: "fileName", sortable: false },
  { title: t("Comment"), value: "comment", sortable: false },
  { title: t("Uploaded at"), value: "updatedAt", sortable: false, width: "65px", headerProps: { class: "text-no-wrap" } },
  { title: "", value: "actions", sortable: false, width: "60px" },
])

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void
  (e: "save", payload: ReportManagementPayload): void
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit("update:modelValue", val),
});

const files = ref<File[]>([]);
const wrongDatatype = ref(false);
const localReports = ref<Report[]>([])
const combinedReports = ref<ReportUI[]>([])
const localOrder = ref<Order | null>(null);
const originalStatusId = ref<string | null>(null);

const statusChanged = computed(() => localOrder.value?.statusId !== originalStatusId.value);
const orderStatusId = computed(() => localOrder.value?.statusId ?? null);

const canEditReportComment = computed(() => {
  return (
    permissionsService.hasPermission(
      PERMISSION.rsNames.orderReport,
      PERMISSION.scopes.update
    ) ||
    permissionsService.hasPermission(
      PERMISSION.rsNames.orderReport,
      PERMISSION.scopes.updateAll
    )
  );
});

const hasRoleExternalUser = computed(() => permissionsService.hasRoleExternalUser)

watch(isOpen, (open) => {
  if (open && props.order) {
    originalStatusId.value = props.order.statusId ?? null
    localOrder.value = { ...props.order } as Order
  } else if (!open) {
    originalStatusId.value = null
  }
})

function getRowProps({ item }: { item: any }) {
  return {
    class: {
      'row--new': item.isNew,
      'row--deleted': item.delete,
      'row--unsaved': item.file instanceof File,
    }
  }
}

function toggleDeleteState(index: number) {
  const report = combinedReports.value[index];
  if (report) {
    report.delete = !report.delete;
  }
}

function buildPayload(): ReportManagementPayload {
  const reports = combinedReports.value as ReportUI[];

  return {
    add: reports
      .filter(r => r.isNew && !r.delete && r.file instanceof File)
      .map(r => ({
        file: r.file as File,
        comment: r.comment ?? ''
      })) as ReportCreatePayload[],

    remove: reports
      .filter(r => !r.isNew && r.delete)
      .map(r => ({
        orderId: r.orderId,
        orderFileId: r.orderFileId
      })) as ReportDeletePayload[],

    update: canEditReportComment.value
      ? reports
    .filter(r => !r.isNew && !r.delete && r.comment !== r.originalComment)
      .map(r => ({
        orderFileId: r.orderFileId,
        comment: r.comment,
        ...(r.file instanceof File ? { file: r.file } : {})
      }) as ReportUpdatePayload)
      : [],

    statusId: localOrder.value?.statusId,
    statusChanged: statusChanged.value,
  };
}

function save() {
  const changedComments = combinedReports.value.filter(
    r => !r.isNew && !r.delete && r.comment !== r.originalComment
  );

  if (changedComments.length && !permissionsService.canEditReportComment) {
    NotificationMessageService.showWarning(
      t("You are not allowed to edit report comments")
    );

    return;
  }

  const payload = buildPayload();
  emit("save", payload);

  setTimeout(() => {
    emit("update:modelValue", false)
  }, 200)
}

function handleInvalidType(invalid: boolean) {
  wrongDatatype.value = invalid;
  if (invalid) {
    console.warn("Detected invalid file type.");
  }
}

function onFilesSelected(newFiles: File[]) {
  if (!newFiles || !newFiles.length) return;

  const reportsToAdd = newFiles.map(f => ({
    file: f,
    fileName: f.name,
    comment: '',
    isNew: true,
    delete: false,
    updatedAt: new Date().toISOString()
  }));

  combinedReports.value.push(...reportsToAdd);
}

function onInvalidFiles(files: File[]) {
  NotificationMessageService.showError(
    t('Invalid file type'),
    files.map(f => f.name).join(', ')
  );
}

watch(
  [() => props.reports, () => props.modelValue],
  ([newReports, isOpen]) => {
    if (isOpen) {
      combinedReports.value = (newReports || []).map(r => ({
        ...r,
        isNew: false,
        delete: r.delete || false,
        comment: r.comment || '',
        originalComment: r.comment || ''
      }));
    }
  },
  { immediate: true, deep: true }
);

watch(isOpen, (isOpen) => {
  if (!isOpen) {
    files.value = [];
    wrongDatatype.value = false;
  }
});

watch(
  () => props.reports,
  (newReports) => {
    localReports.value = newReports.map(r => ({ ...r }))
  },
  { immediate: true, deep: true }
)

watch(
  () => props.order,
  (newOrder) => {
    localOrder.value = newOrder ? { ...newOrder } : null;
  },
  { immediate: true, deep: true }
);
</script>

<style lang="scss">
.report-management-dialog {
  tr.row--new {
    background-color: rgba(var(--v-theme-green), 0.08);
  }

  tr.row--unsaved {
    border-left: 4px solid rgb(var(--v-theme-blue-50));
  }

  tr.row--deleted {
    opacity: 0.5;
  }

  .mark-as-deleted {
    text-decoration: line-through;
    color: rgb(var(--v-theme-black-30));
  }

  .file-name-ellipsis {
    max-width: 150px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: block;
  }
}
</style>
